<script setup lang="ts">
import type {
  FrontendUserAccountDeletionRequestItem,
  FrontendUserAvatarIdentityVerificationItem,
  FrontendUserMessageRiskLogItem,
  FrontendUserProtocolAgreementItem,
} from '@/api/modules/frontendUser'
import type { ProTableColumn } from '@/components/FaProTable/types'
import { FaButton } from '@fantastic-admin/components'
import AppStatusTag from '@/components/AppDisplay/StatusTag.vue'
import FaProTable from '@/components/FaProTable/index.vue'
import { formatDateTime } from '../../utils/userDisplay'
import AvatarIdentityRecords from './AvatarIdentityRecords.vue'

defineOptions({ name: 'UserSafetyTab' })

defineProps<{
  userId: number
  protocols: { list: FrontendUserProtocolAgreementItem[], total: number }
  risks: { list: FrontendUserMessageRiskLogItem[], total: number }
  deletions: { list: FrontendUserAccountDeletionRequestItem[], total: number }
  avatarIdentityVerifications: FrontendUserAvatarIdentityVerificationItem[]
  canNavigate: boolean
  canInvalidateAvatarIdentity: boolean
}>()
const emit = defineEmits<{
  navigate: [path: string, query: Record<string, number>]
  avatarIdentityUpdated: []
}>()

const protocolColumns: ProTableColumn<FrontendUserProtocolAgreementItem>[] = [
  { prop: 'protocol_key', label: '协议', minWidth: 190 },
  { prop: 'version', label: '版本', width: 100 },
  { prop: 'scene', label: '场景', width: 130 },
  { prop: 'platform', label: '平台', width: 100 },
  { prop: 'agreed_at', label: '签收时间', width: 170, formatter: row => formatDateTime(row.agreed_at) },
]
const riskColumns: ProTableColumn<FrontendUserMessageRiskLogItem>[] = [
  { prop: 'id', label: '事件 ID', width: 90 },
  { prop: 'risk_level', label: '风险级别', width: 110, render: ({ row }) => h(AppStatusTag, { label: row.risk_level || '未知', tone: row.risk_level === 'high' ? 'danger' : 'warning' }) },
  { prop: 'reject_reason', label: '命中原因', minWidth: 260 },
  { prop: 'hit_keywords_json', label: '关键词', minWidth: 180, formatter: row => row.hit_keywords_json?.join(' / ') || '-' },
  { prop: 'occurred_at', label: '发生时间', width: 170, formatter: row => formatDateTime(row.occurred_at) },
]
const deletionColumns: ProTableColumn<FrontendUserAccountDeletionRequestItem>[] = [
  { prop: 'request_no', label: '申请单号', minWidth: 210 },
  { prop: 'status', label: '状态', width: 120 },
  { prop: 'reason', label: '申请原因', minWidth: 240 },
  { prop: 'applied_at', label: '申请时间', width: 170, formatter: row => formatDateTime(row.applied_at) },
  { prop: 'processed_at', label: '处理时间', width: 170, formatter: row => formatDateTime(row.processed_at) },
]
</script>

<template>
  <div class="space-y-4">
    <AvatarIdentityRecords
      :user-id="userId"
      :records="avatarIdentityVerifications"
      :can-invalidate="canInvalidateAvatarIdentity"
      @updated="emit('avatarIdentityUpdated')"
    />
    <FaCard title="风险事件" content-class="pt-3">
      <div class="mb-3 flex flex-wrap gap-3 items-center justify-between">
        <span class="text-sm text-muted-foreground">共 {{ risks.total }} 条命中记录</span><FaButton v-if="canNavigate" variant="outline" size="sm" @click="emit('navigate', '/safety/governance', { keyword: userId })">
          进入安全治理
        </FaButton>
      </div>
      <FaProTable :columns="riskColumns" :data="risks.list" :show-toolbar="false" :show-pagination="false" :bordered="false" row-key="id" />
    </FaCard>
    <div class="gap-4 grid xl:grid-cols-2">
      <FaCard title="协议签收" content-class="pt-3">
        <div class="text-sm text-muted-foreground mb-3">
          共 {{ protocols.total }} 条
        </div><FaProTable :columns="protocolColumns" :data="protocols.list" :show-toolbar="false" :show-pagination="false" :bordered="false" row-key="id" />
      </FaCard>
      <FaCard title="注销申请" content-class="pt-3">
        <div class="text-sm text-muted-foreground mb-3">
          共 {{ deletions.total }} 条
        </div><FaProTable :columns="deletionColumns" :data="deletions.list" :show-toolbar="false" :show-pagination="false" :bordered="false" row-key="id" />
      </FaCard>
    </div>
  </div>
</template>
